import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useRealtimeInvalidate } from '@/hooks/useRealtimeInvalidate';
import { signalsApi } from '@/lib/agents';
import { AppShell } from '@/components/app/AppShell';
import { EmptyState } from '@/components/app/EmptyState';
import { Dropdown } from '@/components/ui/Dropdown';
import { HelpHint } from '@/components/app/HelpHint';
import { useDialogs } from '@/components/app/DialogProvider';
import { useFormatDateTime } from '@/lib/datetime';

const KINDS = ['new_doc', 'doc_changed', 'doc_indexed', 'doc_failed', 'term_mentioned', 'entity_resolved', 'retention_expiring'];

/** Render a signal payload as a short human line instead of raw JSON. */
function humanizePayload(p: Record<string, unknown>): string {
  const scalar = (k: string) =>
    typeof p[k] === 'string' || typeof p[k] === 'number' ? String(p[k]) : undefined;
  const parts: string[] = [];
  const label = scalar('title') ?? scalar('name') ?? scalar('filename') ?? scalar('documentTitle');
  if (label) parts.push(label);
  const detail = scalar('status') ?? scalar('reason') ?? scalar('term') ?? scalar('entity');
  if (detail) parts.push(detail);
  if (!parts.length) {
    for (const [k, v] of Object.entries(p)) {
      if (k === 'documentId' || k === 'docId') continue;
      if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') {
        parts.push(`${k}: ${v}`);
        if (parts.length >= 3) break;
      }
    }
  }
  return parts.join(' · ');
}

export function SignalsPage() {
  const { t } = useTranslation();
  const fmt = useFormatDateTime();
  const { wsId } = useParams<{ wsId: string }>();
  const qc = useQueryClient();
  const { alert } = useDialogs();
  // Radix Select rejects empty-string Item values, so the "All" option
  // uses a sentinel and we translate back to undefined for the API.
  const [filterKind, setFilterKind] = useState<string>('__all__');

  const { data: signals } = useQuery({
    queryKey: ['signals', wsId, filterKind],
    queryFn: () => signalsApi.list(wsId!, filterKind === '__all__' ? undefined : filterKind),
    enabled: !!wsId,
  });

  // Push-driven freshness: refetch on a gateway signal/notification event
  // instead of polling every 30s. (filterKind in the key so the live filter view refreshes.)
  useRealtimeInvalidate(['signals', wsId, filterKind]);

  const { data: subscriptions } = useQuery({
    queryKey: ['signal-subs', wsId],
    queryFn: () => signalsApi.listSubscriptions(wsId!),
    enabled: !!wsId,
  });

  const onMutationError = () =>
    alert({ title: t('common.action_failed', { defaultValue: 'Something went wrong. Please try again.' }) as string });

  const subscribe = useMutation({
    mutationFn: (kind: string) => signalsApi.subscribe(wsId!, kind, {}, ['email']),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['signal-subs', wsId] }),
    onError: onMutationError,
  });

  const unsubscribe = useMutation({
    mutationFn: (id: string) => signalsApi.unsubscribe(wsId!, id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['signal-subs', wsId] }),
    onError: onMutationError,
  });

  return (
    <AppShell
      title={t('signals.title', { defaultValue: 'Signals' })}
      description={t('signals.subtitle', { defaultValue: 'Subscribe to knowledge-plane events and review the live feed.' }) as string}
      maxWidth="max-w-4xl"
    >
      <div className="mb-4"><HelpHint page="signals" /></div>
      <div className="space-y-8">
        <section>
          <h2 className="text-sm font-semibold text-ink-900 mb-3">{t('signals.subscriptions', { defaultValue: 'Your subscriptions' })}</h2>
          <div className="flex flex-wrap gap-2">
            {KINDS.map((k) => {
              const sub = subscriptions?.find((s) => s.kind === k);
              return (
                <button
                  key={k}
                  onClick={() => (sub ? unsubscribe.mutate(sub.id) : subscribe.mutate(k))}
                  className={`text-xs px-3 py-1.5 rounded-full transition ${
                    sub
                      ? 'bg-accent-500 text-white shadow-press'
                      : 'bg-paper-100 text-ink-700 hover:text-ink-900 border border-ink-100'
                  }`}
                >
                  {t(`signals.kind.${k}`, { defaultValue: k })}
                  {sub && ' ✓'}
                </button>
              );
            })}
          </div>
        </section>

        <section>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-ink-900">{t('signals.feed', { defaultValue: 'Signal feed' })}</h2>
            <Dropdown
              value={filterKind}
              onChange={(next) => setFilterKind(next)}
              items={[
                { value: '__all__', label: t('common.all', { defaultValue: 'All' }) as string },
                ...KINDS.map((k) => ({
                  value: k,
                  label: t(`signals.kind.${k}`, { defaultValue: k }) as string,
                })),
              ]}
              ariaLabel={t('signals.feed', { defaultValue: 'Signal feed' }) as string}
              size="sm"
            />
          </div>

          {!signals || signals.length === 0 ? (
            <EmptyState
              title={t('signals.empty', { defaultValue: 'No signals yet' }) as string}
              description={t('signals.empty_sub', { defaultValue: 'Signals fire when your knowledge plane changes — a document is indexed, a term is mentioned, retention nears. Add sources or run a workflow to start the feed.' }) as string}
              actions={[
                { label: t('nav.upload', { defaultValue: 'Upload' }) as string, to: `/app/workspace/${wsId}/upload`, primary: true },
                { label: t('nav.connectors', { defaultValue: 'Connectors' }) as string, to: `/app/workspace/${wsId}/connectors` },
                { label: t('workflow.title', { defaultValue: 'Workflows' }) as string, to: `/app/workspace/${wsId}/workflows` },
              ]}
            />
          ) : (
            <ul className="space-y-2">
              {signals.map((s) => {
                const payload = s.payload as Record<string, unknown> | null;
                const docId = (payload?.documentId ?? payload?.docId) as string | undefined;
                return (
                  <li key={s.id} className="card-paper px-4 py-3 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-ink-900">{t(`signals.kind.${s.kind}`, { defaultValue: s.kind })}</span>
                      <span className="text-xs text-ink-500">{fmt(s.created_at)}</span>
                    </div>
                    {payload && humanizePayload(payload) && (
                      <p className="mt-1 text-xs text-ink-600">{humanizePayload(payload)}</p>
                    )}
                    {docId && (
                      <Link to={`/app/workspace/${wsId}/docs/${docId}`} className="mt-1 inline-block text-[12px] text-accent-600 hover:underline">
                        {t('signals.view_doc', { defaultValue: 'View source document →' })}
                      </Link>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      </div>
    </AppShell>
  );
}
